import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Plus,
  Trash2,
  Send,
  Copy,
  Check,
  CalendarDays,
  Video,
} from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import {
  API,
  fmtDate,
  APPT_TYPES,
  dayKey,
} from "./shared";

export function CalendarSyncCard({ authHeader }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const flash = new URLSearchParams(window.location.search).get("calendar");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar/status`, { headers: authHeader() });
      setStatus(res.data);
    } catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/calendar/connect`, {
        headers: authHeader(),
        params: { origin: window.location.origin },
      });
      window.location.href = res.data.authorization_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start Google connection.");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/calendar/disconnect`, {}, { headers: authHeader() });
      await load();
    } catch {} finally { setBusy(false); }
  };

  if (!status) return null;
  return (
    <div data-testid="calendar-sync-card"
      className="bg-white border border-[#BCB3A6]/40 rounded-3xl px-5 py-4 mb-6 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <CalendarDays size={18} className="text-[#899480] shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#3F5043]">Google Calendar sync</div>
          <div className="text-xs text-[#899480]" data-testid="calendar-sync-status">
            {flash === "connected" && "Connected — appointments now sync automatically. "}
            {flash === "error" && "Connection didn't complete — try again. "}
            {!status.configured
              ? "One-time setup in progress — Google credentials are being added."
              : status.connected
                ? `Syncing every appointment to ${status.email}`
                : "Connect once — every new, rescheduled, or canceled appointment updates your calendar automatically."}
          </div>
        </div>
      </div>
      {status.configured && !status.connected && (
        <button onClick={connect} disabled={busy} data-testid="calendar-connect-btn"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CalendarDays size={13} />} Connect Google Calendar
        </button>
      )}
      {status.connected && (
        <button onClick={disconnect} disabled={busy} data-testid="calendar-disconnect-btn"
          className="text-xs rounded-full px-4 py-2 border border-[#875F45]/40 text-[#875F45] hover:bg-[#875F45]/5 transition-colors disabled:opacity-60">
          Disconnect
        </button>
      )}
    </div>
  );
}

export function ApptCard({ a, onDelete, authHeader }) {
  const t = APPT_TYPES[a.type] || APPT_TYPES.consult;
  const [copied, setCopied] = useState(false);
  const [textState, setTextState] = useState("idle"); // idle | sending | sent | nophone | error
  const inviteUrl = `${window.location.origin}/video-visit/${a.id}${a.manage_token ? `?token=${a.manage_token}` : ""}`;
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };
  const textInvite = async () => {
    if (textState === "sending") return;
    setTextState("sending");
    try {
      await axios.post(
        `${API}/admin/appointments/${a.id}/text-invite`,
        { origin_url: window.location.origin },
        { headers: authHeader() }
      );
      setTextState("sent");
    } catch (e) {
      setTextState(e?.response?.status === 400 ? "nophone" : "error");
    } finally {
      setTimeout(() => setTextState((s) => (s === "sending" ? "idle" : s)), 0);
      setTimeout(() => setTextState("idle"), 4000);
    }
  };
  return (
    <div
      data-testid={`appt-item-${a.id}`}
      className="flex items-start justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-5 py-4"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#3F5043]">{a.title}</span>
          <span
            className={`text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full ${t.cls}`}
          >
            {t.label}
          </span>
          {a.is_video && (
            <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#875F45]">
              Video visit
            </span>
          )}
        </div>
        <div className="text-xs text-[#899480] mt-0.5">
          {a.client_email} · {fmtDate(a.date)}
          {a.location ? ` · ${a.location}` : ""}
        </div>
        {a.notes && <div className="text-xs text-[#3F5043]/60 mt-1">{a.notes}</div>}
        {a.is_video && (
          <div className="flex items-center gap-2 mt-2.5 flex-wrap">
            <a
              href={`/video-visit/${a.id}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`appt-join-video-${a.id}`}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold rounded-full px-3 py-1.5 bg-[#3F5043] text-[#F8F7F3] hover:bg-[#875F45] transition-colors"
            >
              <Video size={11} /> Join
            </a>
            <button
              onClick={copyInvite}
              data-testid={`appt-copy-invite-${a.id}`}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors"
            >
              {copied ? <Check size={11} className="text-[#899480]" /> : <Copy size={11} />}
              {copied ? "Invite link copied" : "Copy invite link"}
            </button>
            <button
              onClick={textInvite}
              disabled={textState === "sending"}
              data-testid={`appt-text-invite-${a.id}`}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] font-semibold rounded-full px-3 py-1.5 border border-[#D4AF37]/60 text-[#875F45] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60"
            >
              {textState === "sending" ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              {textState === "sent" ? "Texted to client" : textState === "nophone" ? "No phone on file" : textState === "error" ? "Couldn’t text — retry" : "Text invite"}
            </button>
          </div>
        )}
        {a.key_info && (
          <div className="text-xs mt-1.5 bg-[#875F45]/10 border border-[#875F45]/20 rounded-xl px-3 py-1.5 text-[#3F5043]/80">
            <span className="font-semibold text-[#875F45]">Key info (internal):</span>{" "}
            {a.key_info}
          </div>
        )}
      </div>
      <button
        data-testid={`appt-delete-${a.id}`}
        onClick={() => onDelete(a.id)}
        className="p-2 rounded-full text-[#875F45]/70 hover:bg-[#875F45]/10 flex-shrink-0"
        aria-label="Delete appointment"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

export function AppointmentsTab({ authHeader }) {
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("calendar");
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [form, setForm] = useState({
    client_email: "",
    title: "",
    date: "",
    type: "consult",
    location: "",
    notes: "",
    key_info: "",
    is_video: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers: authHeader() }),
        axios.get(`${API}/admin/appointments`, { headers: authHeader() }),
      ]);
      setClients(c.data);
      setAppts(a.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/appointments`, form, { headers: authHeader() });
      setForm({ client_email: "", title: "", date: "", type: "consult", location: "", notes: "", key_info: "", is_video: false });
      await load();
    } catch {
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/appointments/${id}`, { headers: authHeader() });
      load();
    } catch {}
  };

  const apptDays = appts.filter((a) => a.date).map((a) => new Date(a.date));
  const dayAppts = appts
    .filter((a) => a.date && a.date.slice(0, 10) === dayKey(selectedDay))
    .sort((a, b) => a.date.localeCompare(b.date));

  const inputCls =
    "w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="appointments-tab">
      <div className="mb-6 max-w-xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Appointments</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          Arrange appointments for clients — they appear instantly in the client's portal.
        </p>
      </div>

      <CalendarSyncCard authHeader={authHeader} />

      <form
        onSubmit={create}
        data-testid="appointment-form"
        className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <select
          data-testid="appt-client"
          required
          value={form.client_email}
          onChange={(e) => setForm({ ...form, client_email: e.target.value })}
          className={inputCls}
        >
          <option value="">Select client…</option>
          {clients.map((c) => (
            <option key={c.id} value={c.email}>
              {c.full_name} ({c.email})
            </option>
          ))}
        </select>
        <input
          data-testid="appt-title"
          required
          placeholder="Title (e.g. IVF consult — Dr. Smith)"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className={inputCls}
        />
        <select
          data-testid="appt-type"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
          className={inputCls}
        >
          <option value="consult">Consultation</option>
          <option value="phone">Phone call</option>
          <option value="paid">Paid appointment</option>
          <option value="follow-up">Follow-up</option>
        </select>
        <input
          data-testid="appt-date"
          required
          type="datetime-local"
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className={inputCls}
        />
        <input
          data-testid="appt-location"
          placeholder="Location or video link (optional)"
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          className={inputCls}
        />
        <textarea
          data-testid="appt-notes"
          placeholder="Notes for the client (optional — shown to client)"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className={`${inputCls} sm:col-span-2 resize-none`}
        />
        <textarea
          data-testid="appt-key-info"
          placeholder="Key client info — internal only (e.g. anxieties, cycle day, partner name)"
          rows={2}
          value={form.key_info}
          onChange={(e) => setForm({ ...form, key_info: e.target.value })}
          className={`${inputCls} sm:col-span-2 resize-none border-[#875F45]/40`}
        />
        <label
          className="sm:col-span-2 flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-2xl px-4 py-3 cursor-pointer"
          data-testid="appt-video-toggle"
        >
          <input
            type="checkbox"
            checked={form.is_video}
            onChange={(e) => setForm({ ...form, is_video: e.target.checked })}
            className="w-4 h-4 accent-[#3F5043]"
          />
          <span className="text-xs text-[#3F5043]">
            <span className="font-semibold">Video visit</span> — creates a private video room; the client joins from their portal, and you can copy an invite link to text them.
          </span>
        </label>
        <div className="sm:col-span-2">
          <button
            type="submit"
            data-testid="appt-create-btn"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Schedule appointment
          </button>
        </div>
      </form>

      {/* View toggle */}
      <div className="flex gap-2 mb-5 bg-[#EDEDEE] p-1 rounded-full w-fit">
        {[
          ["calendar", "Calendar"],
          ["list", "List"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`appt-view-${k}`}
            onClick={() => setView(k)}
            className={`px-5 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] font-semibold transition-all ${
              view === k ? "bg-[#3F5043] text-[#F8F7F3]" : "text-[#3F5043]/60"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center text-[#899480] py-10">
          <Loader2 size={18} className="animate-spin inline" /> Loading…
        </div>
      ) : view === "calendar" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div
            className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-4 flex justify-center"
            data-testid="appt-calendar"
          >
            <Calendar
              mode="single"
              selected={selectedDay}
              onSelect={(d) => d && setSelectedDay(d)}
              modifiers={{ hasAppt: apptDays }}
              modifiersClassNames={{
                hasAppt: "font-bold text-[#875F45] underline underline-offset-4",
              }}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#899480] font-semibold mb-3">
              Schedule ·{" "}
              {selectedDay.toLocaleDateString("en-US", {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </div>
            {dayAppts.length === 0 ? (
              <div
                className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-8 text-center text-sm text-[#899480]"
                data-testid="appt-day-empty"
              >
                No appointments this day.
              </div>
            ) : (
              <div className="space-y-2">
                {dayAppts.map((a) => (
                  <ApptCard key={a.id} a={a} onDelete={remove} authHeader={authHeader} />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : appts.length === 0 ? (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center" data-testid="appts-empty">
          <CalendarDays size={26} strokeWidth={1.3} className="mx-auto text-[#899480] mb-3" />
          <div className="font-serif text-xl text-[#3F5043]">No appointments scheduled.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {appts.map((a) => (
            <ApptCard key={a.id} a={a} onDelete={remove} authHeader={authHeader} />
          ))}
        </div>
      )}
    </div>
  );
}
